import React from 'react';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { get } from 'lodash-es';

import withLoader from '../../components/Loader/withLoader.js';
import { getLoadedStatus } from '../../helpers/loaderHelpers';
import { hasRole } from '../../helpers/authHelpers.js';
import { isFeatureDisabled } from '../../helpers/outageHelpers';
import { getString } from '../../helpers/stringHelpers.js';
import constants from '../../constants.js';

// Actions
import { actions as routingActions } from '../../ducks/routing.js';
import { actions as authActions } from '../../ducks/auth.js';
import { actions as appActions } from '../../ducks/app.js';
import { actions as customerActions } from '../../ducks/customer.js';

import TenancyInformationSection from './sections/TenancyInformationSection/TenancyInformationSection';
import ContactDetailsSection from './sections/ContactDetailsSection/ContactDetailsSection';
import EmergencyContactsSection from './sections/EmergencyContactsSection/EmergencyContactsSection';
import BankDetailsSection from './sections/BankDetailsSection/BankDetailsSection';
import ConsentedPersonsSection from './sections/ConsentedPersonsSection/ConsentedPersonsSection';
import NotificationsSection from './sections/NotificationsSection/NotificationsSection';
import ProfileChildLayout from './ProfileChildLayout';
import ReplaceContact from '../../components/Replace/ReplaceContact/ReplaceContact';

const stringsComponent = 'contactDetails';

class ProfileChild extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hasLoaded: false,
    };

    this.getLoadedStatuses = this.getLoadedStatuses.bind(this);
    this.setHasLoaded = this.setHasLoaded.bind(this);
  }

  getLoadedStatuses(props = this.props) {
    const { tenancy, contactDetails, bankDetails, roles, preferences } = props;
    const loaded = [contactDetails.loaded, tenancy.loaded, preferences.loaded];
    if (hasRole(roles, constants.ROLE_BANK_READ)) {
      loaded.push(bankDetails.loaded);
    }
    return loaded;
  }

  setHasLoaded() {
    const { hasLoaded } = this.state;

    if (
      getLoadedStatus(this.getLoadedStatuses(this.props)) == 'LOADED' &&
      !hasLoaded
    ) {
      // Once all required data has loaded, store that fact.
      this.setState({ hasLoaded: true });
    }
  }

  componentDidMount() {
    const { confirmedOpen, setConfirmedOpen, onLoadComplete } = this.props;

    this.setHasLoaded();

    // Confirmed dialog should not show on component mount.
    // This can occur when the contact details are updated outside the profile
    // child section.
    if (confirmedOpen) {
      setConfirmedOpen();
    }

    onLoadComplete();
  }

  componentDidUpdate() {
    this.setHasLoaded();
  }

  shouldComponentUpdate(nextProps) {
    // Only rerender if not in a unloaded status or the loading has not
    // previously completed.
    const { hasLoaded } = this.state;

    const { preferences } = this.props;

    if (
      getLoadedStatus(this.getLoadedStatuses(nextProps)) !== 'UNLOADED' ||
      hasLoaded
    ) {
      // This fix prevents page from scrolling to top upon toggling notification preferences
      if (
        JSON.stringify(preferences.newNotificationPreferences) ===
        JSON.stringify(nextProps.preferences.newNotificationPreferences)
      ) {
        return true;
      }
    }
    return false;
  }

  render() {
    const {
      signout,
      inOutage,
      outageFrom,
      outageTo,
      outageMessage,
      page,
      goProfile,
      tenancy,
      customerName,
      partyRef,
      contactDetails,
      updateContact,
      deleteContact,
      setConfirmedOpen,
      confirmedOpen,
      setContactToDelete,
      bankDetails,
      updateBankAccount,
      createBankAccount,
      deleteBankAccount,
      createConsentedPerson,
      deleteConsentedPerson,
      toggleContactModal,
      roles,
      createPreferences,
      preferences,
    } = this.props;

    const tenancyData = tenancy.data;
    const contactData = contactDetails.data.contactDetails;
    const emergencyContactData = contactDetails.data.emergencyContacts;
    const consentedPersonsData = contactDetails.data.consentedPersons;
    const preferencesData = preferences.data.preferences;
    const newNotificationPreferences = preferences.newNotificationPreferences;

    let section = null;
    let title = '';
    let summary = null;
    let summary2 = null;

    const canEdit = !isFeatureDisabled('PROFILE_WRITE', tenancyData.area);

    if (getLoadedStatus(this.getLoadedStatuses()) == 'LOADED') {
      switch (page) {
        case 'Tenancy-Information':
          {
            section = (
              <TenancyInformationSection
                tenancyData={tenancyData}
                name={customerName}
                reference={partyRef}
              />
            );
          }
          title = getString(stringsComponent, 'TENANCY_INFORMATION_TITLE');
          break;

        case 'Contact-Details':
          {
            section = (
              <ContactDetailsSection
                contactData={contactData}
                updateContact={updateContact}
                setConfirmedOpen={setConfirmedOpen}
                confirmedOpen={confirmedOpen}
                canEdit={
                  canEdit && hasRole(roles, constants.ROLE_CUSTOMER_WRITE)
                }
              />
            );
          }
          title = getString(stringsComponent, 'CONTACT_DETAILS_TITLE');
          break;

        case 'Emergency-Contacts':
          {
            section = (
              <EmergencyContactsSection
                emergencyContactData={emergencyContactData}
                updateContact={updateContact}
                deleteContact={deleteContact}
                setConfirmedOpen={setConfirmedOpen}
                confirmedOpen={confirmedOpen}
                setContactToDelete={setContactToDelete}
                canEdit={
                  canEdit && hasRole(roles, constants.ROLE_CUSTOMER_WRITE)
                }
              />
            );
          }
          title = getString(stringsComponent, 'EMERGENCY_CONTACTS_TITLE');
          summary = getString(stringsComponent, 'EMERGENCY_CONTACT_HELP_TEXT');
          break;

        case 'Account-Details':
          {
            section = (
              <BankDetailsSection
                bankDetails={bankDetails}
                updateBankAccount={updateBankAccount}
                createBankAccount={createBankAccount}
                deleteBankAccount={deleteBankAccount}
                tenancyData={tenancyData}
                contactData={contactData}
                confirmedOpen={confirmedOpen}
                setConfirmedOpen={setConfirmedOpen}
                customerName={customerName}
                toggleContactModal={toggleContactModal}
                canEdit={canEdit && hasRole(roles, constants.ROLE_BANK_WRITE)}
              />
            );
          }
          title = getString(stringsComponent, 'ACCOUNT_DETAILS_TITLE');
          break;

        case 'Consented-Persons':
          {
            section = (
              <ConsentedPersonsSection
                confirmedOpen={confirmedOpen}
                setConfirmedOpen={setConfirmedOpen}
                consentedPersonsData={consentedPersonsData}
                createConsentedPerson={createConsentedPerson}
                deleteConsentedPerson={deleteConsentedPerson}
                canEdit={
                  canEdit && hasRole(roles, constants.ROLE_CUSTOMER_WRITE)
                }
              />
            );
          }
          title = getString(stringsComponent, 'CONSENTED_PERSONS_TITLE');
          summary = getString(stringsComponent, 'CONSENTED_PERSONS_SUMMARY');
          break;

        case 'Notifications':
          {
            section = (
              <NotificationsSection
                createPreferences={createPreferences}
                preferencesData={preferencesData}
                newNotificationPreferences={newNotificationPreferences}
                contactData={contactData}
              />
            );
          }
          title = getString('notifications', 'NOTIFICATIONS_TITLE');
          summary = getString(stringsComponent, 'NOTIFICATIONS_SUMMARY');
          summary2 = getString(stringsComponent, 'NOTIFICATIONS_SUMMARY_2');
          break;

        default:
          break;
      }
    }

    const WrappedProfileChildLayout = withLoader(ProfileChildLayout);

    return (
      <WrappedProfileChildLayout
        onError={signout}
        inOutage={inOutage}
        outageFrom={outageFrom}
        outageTo={outageTo}
        outageMessage={
          outageMessage && (
            <ReplaceContact
              content={outageMessage}
              action={toggleContactModal}
              isLink={true}
            />
          )
        }
        failureText={
          <ReplaceContact
            content={portalContent.APP_ERROR_MESSAGE}
            action={toggleContactModal}
            isLink={true}
          />
        }
        notFoundText={portalContent.APP_NOT_FOUND_MESSAGE}
        loaded={this.getLoadedStatuses()}
        backAction={goProfile}
        section={section}
        title={title}
        summary={summary}
        summary2={summary2}
      />
    );
  }
}

const mapStateToProps = (state) => ({
  page: state.location.payload.page,
  partyRef: state.auth.reference,
  roles: state.auth.roles,
  contactDetails: state.customer.contactDetails,
  tenancy: state.customer.tenancy,
  bankDetails: state.customer.bankDetails,
  customerName: `${state.auth.firstName} ${state.auth.lastName}`,
  confirmedOpen: state.app.confirmedOpen,
  preferences: state.customer.preferences,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...routingActions,
      ...appActions,
      ...authActions,
      ...customerActions,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(ProfileChild);
